import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { ReactElement, useEffect, useState } from 'react'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import axios from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchTitleScreens,
  fetchVideos,
  fetchTextContent,
  fetchQuizzes,
  fetchStoryboardSteps,
  selectTitleScreenByClassID,
  selectVideosByClassID,
  selectTextContentByClassID,
  selectQuizByClassID,
  selectStoryboardStepsByClassID,
  selectStoryboardStepsStatus,
} from 'src/v3/create/create.slice'
import {
  AddIcon,
  LeftArrowIcon,
  RightArrowIcon,
  RemoveIcon,
  SaveIcon,
  TitleScreenIcon,
  VideoIcon,
  TextContentIcon,
  QuizIcon,
} from 'src/v3/core/icons'
import { ClassUpdated as Class, StreamStep } from 'src/v3/core/types'
import { v4 as uuidv4 } from 'uuid'
import { useNavigate } from 'react-router-dom'

interface Props {
  selectedClass?: Class
  setStep: React.Dispatch<
    React.SetStateAction<
      | 'basic-info'
      | 'title-screen'
      | 'content'
      | 'quiz'
      | 'storyboard'
      | 'preview'
    >
  >
}

let moveItem = (list: any[], oldIndex: number, newIndex: number) => {
  let without = list.filter((x, i) => i !== oldIndex)
  return [
    ...without.slice(0, newIndex),
    list[oldIndex],
    ...without.slice(newIndex),
  ]
}

export default ({ selectedClass, setStep }: Props): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()

  let titleScreen = useAppSelector(
    selectTitleScreenByClassID(selectedClass?.id || ''),
  )
  let videos =
    useAppSelector(selectVideosByClassID(selectedClass?.id || '')) || []
  let textContent =
    useAppSelector(selectTextContentByClassID(selectedClass?.id || '')) || []
  let quiz = useAppSelector(selectQuizByClassID(selectedClass?.id || ''))
  let existingStoryboard =
    useAppSelector(selectStoryboardStepsByClassID(selectedClass?.id || '')) ||
    []
  let storyboardStatus = useAppSelector(selectStoryboardStepsStatus)

  // Create new steps for videos and text content not yet in storyboard
  let newSteps: Partial<StreamStep>[] = [
    ...textContent
      .filter(
        (tc) => !existingStoryboard.some((ss) => ss.textContentID === tc.id),
      )
      .map((tc) => ({ id: uuidv4(), textContentID: tc.id })),
    ...videos
      .filter((v) => !existingStoryboard.some((ss) => ss.videoID === v.id))
      .map((v) => ({ id: uuidv4(), videoID: v.id })),
  ]

  let [storyboard, setStoryboard] =
    useState<Partial<StreamStep>[]>(existingStoryboard)
  let [unusedSteps, setUnusedSteps] = useState<Partial<StreamStep>[]>(newSteps)
  let [saving, setSaving] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    if (selectedClass) {
      dispatch(fetchTitleScreens(selectedClass.id))
      dispatch(fetchVideos())
      dispatch(fetchTextContent(selectedClass.id))
      dispatch(fetchQuizzes(selectedClass.id))
      dispatch(fetchStoryboardSteps())
    }
  }, [selectedClass])

  useEffect(() => {
    if (storyboardStatus === 'fulfilled') {
      setStoryboard(existingStoryboard)
      setUnusedSteps(newSteps)
    }
  }, [storyboardStatus, existingStoryboard.length, newSteps.length])

  let getStepDetails = (step: any) => {
    // Check by type field first, then fallback to ID fields
    if (step.type === 'titleScreen' || step.titleScreenID) {
      return {
        name: `${selectedClass?.name} Title Screen`,
        icon: <TitleScreenIcon />,
        isDraggable: false,
      }
    }
    if (step.type === 'video' || step.videoID) {
      let video = videos.find((v) => v.id === step.videoID)
      return {
        name: video?.name || 'Unknown Video',
        icon: <VideoIcon />,
        isDraggable: true,
      }
    }
    if (step.type === 'textContent' || step.textContentID) {
      let content = textContent.find((tc) => tc.id === step.textContentID)
      return {
        name: content?.name || 'Unknown Text Content',
        icon: <TextContentIcon />,
        isDraggable: true,
      }
    }
    if (step.type === 'quiz' || step.quizID) {
      return {
        name: `${selectedClass?.name} Quiz`,
        icon: <QuizIcon />,
        isDraggable: false,
      }
    }
    return {
      name: `Unknown Step (${step.type || 'no type'})`,
      icon: null,
      isDraggable: false,
    }
  }

  let onDragEnd = (result: any) => {
    if (
      !result.destination ||
      result.destination.index === 0 ||
      result.destination.index === storyboard.length - 1
    ) {
      return // Can't move to first or last position (reserved for title screen and quiz)
    }

    setStoryboard(
      moveItem(storyboard, result.source.index, result.destination.index),
    )
  }

  let removeFromStoryboard = (step: Partial<StreamStep>) => {
    setStoryboard(storyboard.filter((s) => s.id !== step.id))
    setUnusedSteps([...unusedSteps, step])
  }

  let addToStoryboard = (step: Partial<StreamStep>) => {
    // Insert before the quiz (last item)
    setStoryboard([
      ...storyboard.slice(0, storyboard.length - 1),
      step,
      storyboard[storyboard.length - 1],
    ])
    setUnusedSteps(unusedSteps.filter((s) => s.id !== step.id))
  }

  let saveStoryboard = async () => {
    if (!selectedClass) return

    setSaving(true)
    try {
      await axios.post('/api/v2/create/stream', {
        classID: selectedClass.id,
        steps: [
          { titleScreen: { titleScreenID: titleScreen?.id } },
          ...storyboard
            .slice(1, storyboard.length - 1)
            .map((s) =>
              s.videoID
                ? { video: { videoID: s.videoID } }
                : { textContent: { textContentID: s.textContentID } },
            ),
          { quiz: { quizID: quiz?.id } },
        ],
      })

      await dispatch(fetchStoryboardSteps())
      setErrorMessage('')
    } catch (err: any) {
      setErrorMessage(
        err?.response?.data || err?.message || 'Error saving storyboard',
      )
    } finally {
      setSaving(false)
    }
  }

  let createInitialStoryboard = async () => {
    if (!selectedClass || !titleScreen || !quiz) return

    setSaving(true)
    try {
      await axios.post('/api/v2/create/stream', {
        classID: selectedClass.id,
        steps: [
          { titleScreen: { titleScreenID: titleScreen.id } },
          ...videos.map((v) => ({ video: { videoID: v.id } })),
          ...textContent.map((tc) => ({
            textContent: { textContentID: tc.id },
          })),
          { quiz: { quizID: quiz.id } },
        ],
      })

      await dispatch(fetchStoryboardSteps())
      setErrorMessage('')
    } catch (err: any) {
      setErrorMessage(
        err?.response?.data || err?.message || 'Error creating storyboard',
      )
    } finally {
      setSaving(false)
    }
  }

  if (!selectedClass) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Please complete the Basic Info step first to manage the storyboard.
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            variant="outlined"
            startIcon={<LeftArrowIcon />}
            onClick={() => setStep('basic-info')}
          >
            Back to Basic Info
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h3" sx={{ mb: 3 }}>
        Storyboard
      </Typography>

      <Typography variant="body1" sx={{ mb: 4 }}>
        Arrange the class content flow. The title screen is always first and the
        quiz is always last. You can drag and drop videos and text content to
        reorder them in between.
      </Typography>

      {!existingStoryboard.length ? (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            No storyboard exists for this class yet.
          </Alert>

          {!titleScreen && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Before a storyboard can be created, this class needs a title
              screen.
            </Alert>
          )}

          {!quiz && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Before a storyboard can be created, this class needs a quiz.
            </Alert>
          )}

          {titleScreen && quiz && (
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              startIcon={<AddIcon />}
              onClick={createInitialStoryboard}
              disabled={saving}
              sx={{ mb: 3 }}
            >
              {saving ? 'Creating...' : 'Create Storyboard'}
            </Button>
          )}
        </Box>
      ) : (
        <Box>
          {/* Current Storyboard */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Current Storyboard ({storyboard.length} steps)
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Drag and drop videos and text content to reorder them. Title
                screen and quiz positions are locked.
              </Typography>

              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="storyboard">
                  {(provided) => (
                    <Box {...provided.droppableProps} ref={provided.innerRef}>
                      {storyboard.map((step, index) => {
                        let { name, icon, isDraggable } = getStepDetails(step)

                        return (
                          <Draggable
                            key={step.id || `step-${index}`}
                            draggableId={step.id || `step-${index}`}
                            index={index}
                            isDragDisabled={!isDraggable}
                          >
                            {(p) => (
                              <Card
                                ref={p.innerRef}
                                {...p.draggableProps}
                                {...p.dragHandleProps}
                                sx={{
                                  p: 2,
                                  mb: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  cursor: isDraggable ? 'grab' : 'default',
                                  bgcolor: 'backgorund.default',
                                  '&:active': {
                                    cursor: isDraggable
                                      ? 'grabbing'
                                      : 'default',
                                  },
                                }}
                                variant="outlined"
                              >
                                {icon}
                                <Typography sx={{ flexGrow: 1, ml: 2 }}>
                                  {index + 1}. {name}
                                </Typography>
                                {!isDraggable && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontStyle: 'italic' }}
                                  >
                                    (locked)
                                  </Typography>
                                )}
                                {isDraggable && (
                                  <Tooltip title="Remove from Storyboard">
                                    <IconButton
                                      color="error"
                                      onClick={() => removeFromStoryboard(step)}
                                    >
                                      <RemoveIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Card>
                            )}
                          </Draggable>
                        )
                      })}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>
            </CardContent>
          </Card>

          {/* Unused Content */}
          {unusedSteps.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Available Content
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  This content has been created but not yet added to the
                  storyboard. Click the plus button to add it.
                </Typography>

                {unusedSteps.map((step, index) => {
                  let { name, icon } = getStepDetails(step)

                  return (
                    <Card
                      key={step.id || `unused-${index}`}
                      sx={{
                        p: 2,
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      variant="outlined"
                    >
                      {icon}
                      <Typography sx={{ flexGrow: 1, ml: 2 }}>
                        {name}
                      </Typography>
                      <Tooltip title="Add to Storyboard">
                        <IconButton
                          color="primary"
                          onClick={() => addToStoryboard(step)}
                        >
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    </Card>
                  )
                })}
              </CardContent>
            </Card>
          )}

          {/* Save Button */}
          <Button
            variant="contained"
            size="large"
            color="secondary"
            startIcon={<SaveIcon />}
            onClick={saveStoryboard}
            disabled={saving}
            sx={{ mb: 3 }}
          >
            {saving ? 'Saving...' : 'Save Storyboard'}
          </Button>
        </Box>
      )}

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/create/v4')}
            disabled={saving}
          >
            Save and Exit
          </Button>
          <Button
            variant="outlined"
            startIcon={<LeftArrowIcon />}
            onClick={() => setStep('quiz')}
          >
            Back to Quiz
          </Button>
        </Box>

        <Button
          variant="contained"
          endIcon={<RightArrowIcon />}
          onClick={() => setStep('preview')}
        >
          Continue to Preview
        </Button>
      </Box>

      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}
